import { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import axios from 'axios';

import {
  COUNT_ONLY_DATA_TYPES,
  VENUE_DETAIL_FROM_ENTITY_DATA_TYPES,
  getEntityConfiguration,
  getEntityDisplayName,
} from './configurations';
import { EntityType, GenericMergeDialogProps, MergeableEntity, MergeEntityData } from './types';
import { GenericEntitySection } from './GenericEntitySection';

export const GenericMergeDialog = ({
  open,
  entities,
  entityType,
  onClose,
  onConfirm,
}: GenericMergeDialogProps) => {
  const [selectedRealEntityId, setSelectedRealEntityId] = useState<number | null>(null);
  const [entityData, setEntityData] = useState<{
    [entityId: number]: MergeEntityData;
  }>({});

  useEffect(() => {
    if (open && entities.length === 2 && entityType) {
      setSelectedRealEntityId(entities[0].id);

      const initialData = entities.reduce(
        (acc, entity) => {
          const config = getEntityConfiguration(entityType);
          const dataStructure: MergeEntityData = { loading: true };

          Object.keys(config).forEach(dataType => {
            dataStructure[dataType] = {
              items: [],
              totalItems: 0,
              page: 1,
              pageSize: 20,
            };
          });

          acc[entity.id] = dataStructure;
          return acc;
        },
        {} as typeof entityData,
      );

      setEntityData(initialData);

      const fetchEntityData = async () => {
        try {
          const config = getEntityConfiguration(entityType);

          if (Object.keys(config).length === 0) {
            setEntityData(prev => {
              const newData = { ...prev };
              entities.forEach(entity => {
                newData[entity.id] = { loading: false };
              });
              return newData;
            });
            return;
          }

          const entityDataPromises = entities.map(async entity => {
            const dataPromises = Object.entries(config).map(async ([dataType, urlTemplate]) => {
              try {
                if (dataType === 'lineups') {
                  const response = await axios.get(
                    `/competitors/players/${entity.id}/lineups/count`,
                  );
                  const { lineupsCount, latestFixtures } = parseLineupsResponse(response.data);
                  return {
                    dataType,
                    data: {
                      items: latestFixtures,
                      totalItems: lineupsCount,
                      page: 1,
                      pageSize: 20,
                      isCountOnly: true,
                    },
                  };
                }

                if (VENUE_DETAIL_FROM_ENTITY_DATA_TYPES.has(dataType)) {
                  const response = await axios.get(`/venues/${entity.id}`);
                  const sports = Array.isArray(response.data?.sports) ? response.data.sports : [];
                  return {
                    dataType,
                    data: {
                      items: sports,
                      totalItems: sports.length,
                      page: 1,
                      pageSize: sports.length || 20,
                    },
                  };
                }

                let url = urlTemplate
                  .replace('{entityId}', entity.id.toString())
                  .replace('{page}', '1')
                  .replace('{entityType}', getEntityTypeForUrl(entityType));

                if (dataType === 'contracts' || dataType === 'officials') {
                  const entityRes = await axios.get(
                    `/competitors/${getEntityTypeForUrl(entityType)}/${entity.id}`,
                  );
                  const personId = entityRes.data?.person?.id;
                  if (personId) {
                    url = url.replace('{personId}', personId.toString());
                  } else {
                    return {
                      dataType,
                      data: { items: [], totalItems: 0, page: 1, pageSize: 20 },
                    };
                  }
                }

                if (dataType === 'rounds') {
                  url = `/competitors/${getEntityTypeForUrl(entityType)}/${entity.id}/rounds?pageSize=20&page=1`;
                }

                const response = await axios.get(url);
                return {
                  dataType,
                  data: {
                    items: response.data?.items || [],
                    totalItems: response.data?.totalItems || 0,
                    page: response.data?.page || 1,
                    pageSize: response.data?.pageSize || 20,
                  },
                };
              } catch (error) {
                console.error(`Error fetching ${dataType} for entity ${entity.id}:`, error);
                return {
                  dataType,
                  data: { items: [], totalItems: 0, page: 1, pageSize: 20 },
                };
              }
            });

            const results = await Promise.all(dataPromises);
            const entityDataResult: MergeEntityData = { loading: false };

            results.forEach(({ dataType, data }) => {
              entityDataResult[dataType] = data;
            });

            return {
              entityId: entity.id,
              data: entityDataResult,
            };
          });

          const entityResults = await Promise.all(entityDataPromises);

          setEntityData(prev => {
            const newData = { ...prev };
            entityResults.forEach(({ entityId, data }) => {
              newData[entityId] = data;
            });
            return newData;
          });
        } catch (error) {
          console.error('Error fetching entity data:', error);
          setEntityData(prev => {
            const newData = { ...prev };
            entities.forEach(entity => {
              newData[entity.id] = { loading: false };
            });
            return newData;
          });
        }
      };

      fetchEntityData();
    }
  }, [open, entities, entityType]);

  const handleConfirm = () => {
    if (selectedRealEntityId) {
      const fakeId = entities.find(e => e.id !== selectedRealEntityId)?.id;
      if (fakeId) {
        onConfirm({ realId: selectedRealEntityId, fakeId });
      }
    }
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRealEntityId(Number(event.target.value));
  };

  const loadMoreData = async (entityId: number, dataType: string) => {
    if (COUNT_ONLY_DATA_TYPES.has(dataType) || VENUE_DETAIL_FROM_ENTITY_DATA_TYPES.has(dataType)) {
      return;
    }

    const currentData = entityData[entityId]?.[dataType];
    if (!currentData || typeof currentData === 'boolean') {
      return;
    }

    const nextPage = currentData.page + 1;
    const hasMoreData = currentData.items.length < currentData.totalItems;

    if (!hasMoreData) {
      return;
    }

    try {
      const config = getEntityConfiguration(entityType);
      const urlTemplate = config[dataType];

      if (!urlTemplate) {
        return;
      }

      let url = urlTemplate
        .replace('{entityId}', entityId.toString())
        .replace('{page}', nextPage.toString())
        .replace('{entityType}', getEntityTypeForUrl(entityType));

      if (dataType === 'contracts' || dataType === 'officials') {
        const entityRes = await axios.get(
          `/competitors/${getEntityTypeForUrl(entityType)}/${entityId}`,
        );
        const personId = entityRes.data?.person?.id;
        if (personId) {
          url = url.replace('{personId}', personId.toString());
        } else {
          return;
        }
      }

      if (dataType === 'rounds') {
        url = `/competitors/${getEntityTypeForUrl(entityType)}/${entityId}/rounds?pageSize=20&page=${nextPage}`;
      }

      const response = await axios.get(url);
      const newItems = response.data?.items || [];

      setEntityData(prev => ({
        ...prev,
        [entityId]: {
          ...prev[entityId],
          [dataType]: {
            items: [...currentData.items, ...newItems],
            totalItems: response.data?.totalItems || currentData.totalItems,
            page: nextPage,
            pageSize: response.data?.pageSize || 20,
          },
        },
      }));
    } catch (error) {
      console.error(`Error loading more ${dataType}:`, error);
    }
  };

  const renderEntitySection = (entity: MergeableEntity) => {
    const data = entityData[entity.id];
    const isSelected = selectedRealEntityId === entity.id;

    return (
      <GenericEntitySection
        key={entity.id}
        entity={entity}
        entityType={entityType}
        data={data}
        isSelected={isSelected}
        onRadioChange={handleRadioChange}
        onLoadMore={loadMoreData}
      />
    );
  };

  if (!entityType) {
    return null;
  }

  const entityDisplayName = getEntityDisplayName(entityType);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Merge {entityDisplayName}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            You are about to merge the following 2 {entityDisplayName.toLowerCase()}. Select which{' '}
            {entityDisplayName.toLowerCase().slice(0, -1)} should be kept as the &quot;real&quot;
            one. This action cannot be undone.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {entities.map(entity => (
            <Grid item xs={6} key={entity.id}>
              {renderEntitySection(entity)}
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={!selectedRealEntityId}
        >
          Confirm Merge
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const getEntityTypeForUrl = (entityType: EntityType): string => {
  const urlMap: Record<EntityType, string> = {
    players: 'players',
    teams: 'teams',
    doubles: 'doubles',
    horses: 'horses',
    organizations: 'organizations',
    competitions: 'competitions',
    seasons: 'seasons',
    rounds: 'rounds',
    fixtures: 'fixtures',
    persons: 'persons',
    venues: 'venues',
  };

  return urlMap[entityType];
};

const parseLineupsResponse = (
  data: unknown,
): { lineupsCount: number; latestFixtures: Array<{ id: number; startDate?: string }> } => {
  if (!data || typeof data !== 'object') {
    return { lineupsCount: 0, latestFixtures: [] };
  }

  const record = data as {
    lineupsCount?: unknown;
    latestFixtures?: unknown;
  };

  const lineupsCount = typeof record.lineupsCount === 'number' ? record.lineupsCount : 0;
  const latestFixtures = Array.isArray(record.latestFixtures)
    ? record.latestFixtures.filter(
        (fixture): fixture is { id: number; startDate?: string } =>
          fixture != null &&
          typeof fixture === 'object' &&
          typeof (fixture as { id?: unknown }).id === 'number',
      )
    : [];

  return { lineupsCount, latestFixtures };
};
